import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Modal,
  TextField,
  CircularProgress,
} from "@mui/material";
import api, { API_BASE_URL } from "../../../utils/api";

const ArchitecturalRequestVisits = ({ architectural_request_id, currentUserId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await api.get(`/getEvents`);
        const filteredEvents = response.data.filter(event => event.architectural_request_id === Number(architectural_request_id));
        setEvents(filteredEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };

    if (architectural_request_id) {
      fetchEvents();
    }
  }, [architectural_request_id]);

  const fetchComments = async (eventId) => {
    setIsCommentsLoading(true);
    try {
      const response = await api.get(`/getArchitecturalComments/${architectural_request_id}/${eventId}`);
      setComments(response.data);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    } finally {
      setIsCommentsLoading(false);
    }
  };

  const handleOpenComments = (eventId) => {
    setSelectedEventId(eventId);
    setIsModalOpen(true);
    fetchComments(eventId);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      alert("Comment cannot be empty.");
      return;
    }

    try {
      await api.post(`/addArchitecturalComment`, {
        architectural_request_id,
        event_id: selectedEventId,
        user_id: currentUserId,
        comment_text: newComment,
        is_internal: false,
      });

      setComments((prevComments) => [
        {
          comment_text: newComment,
          sent_at: new Date().toISOString(),
          user_name: "You",
          user_lastname: "",
        },
        ...prevComments,
      ]);

      setNewComment("");
    } catch (error) {
      console.error("Failed to add comment:", error);
      alert("Failed to add comment. Please try again.");
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Visits Related to Architectural Request ID: {architectural_request_id}
      </Typography>
      {loading ? (
        <Typography>Loading...</Typography>
      ) : events.length === 0 ? (
        <Typography>No visits found.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Architectural Request ID</TableCell>
                <TableCell>Event ID</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map(event => (
                <TableRow key={event.id}>
                  <TableCell>{event.architectural_request_id}</TableCell>
                  <TableCell>{event.id}</TableCell>
                  <TableCell>{event.title}</TableCell>
                  <TableCell>{event.description}</TableCell>
                  <TableCell>{new Date(event.start_time).toLocaleString()}</TableCell>
                  <TableCell>{new Date(event.end_time).toLocaleString()}</TableCell>
                  <TableCell>{event.created_by_user_id}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleOpenComments(event.id)}
                    >
                      View/Add Comments
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal for Viewing and Adding Comments */}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="comments-modal"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            backgroundColor: "white",
            padding: 4,
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <Typography id="comments-modal" variant="h6" sx={{ mb: 2 }}>
            Comments for Architectural Request ID: {architectural_request_id}, Event ID: {selectedEventId}
          </Typography>
          {isCommentsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", marginBottom: 2 }}>
              <CircularProgress />
            </Box>
          ) : comments.length > 0 ? (
            comments.map((comment, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                }}
              >
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {comment.comment_text}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Added by {comment.user_name} {comment.user_lastname || ""} on{" "}
                  {new Date(comment.sent_at).toLocaleString()}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography>No comments available.</Typography>
          )}

          <TextField
            fullWidth
            label="Add a Comment"
            multiline
            rows={3}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
          />
          <Button fullWidth variant="contained" color="primary" onClick={handleAddComment}>
            Save Comment
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default ArchitecturalRequestVisits;
